import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
// @ts-strict-ignore
import { Classes, ControlGroup, Icon, Position } from '@blueprintjs/core'
import { Menu, MenuDivider, MenuItem } from '../../../components/ui/menu'
import { HTMLSelect } from '../../../components/ui/html-select'
import { PopoverPosition } from '@blueprintjs/core/lib/esm/components/popover/popoverSharedProps'
import { Dialog } from '../../../components/ui/dialog'
import { Popover } from '../../../components/ui/popover'
import classNames from 'classnames'
import { format } from 'date-fns'
import { observer } from 'mobx-react-lite'
import React, { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react'
import { TextInput } from '../../../components/inputs/Input'
import { FilterI } from '../../../constants/filter'
import { useFavoriteFilters } from '../../../hooks/useFavoriteFilters'
import { FilterIcon } from '../../../icons/icons'
import { FiltersMenuProps } from '../../../types/filter'
import { Column } from '../../../types/table'
import { EditableField } from './EditableField'
import { FilterDate } from './FilterDate'
import { FilterNumber } from './FilterNumber'
import { MyHTMLTable } from './HTMLTable'
import { useFilter } from './useFilter'

interface FilterProps {
    onChange?: any
    filters: FilterI[]
    defaultFilter?: any
    Filters: any
    customFilters?: any
    saveFilter?: any
    enableSave?: boolean
    enableManage?: boolean
    showOptions?: true
    editCustomFilter?: any
    deleteCustomFilter?: any
    disabled?: boolean
    openState?: boolean
    setOpenState?: Dispatch<SetStateAction<boolean>>
    disableSavedFilters?: boolean
    menuPopoverPosition?: PopoverPosition
    scrollable?: boolean
}

export const Filter = observer(
    ({
        onChange,
        filters,
        defaultFilter,
        Filters,
        customFilters,
        saveFilter,
        enableSave = true,
        enableManage = true,
        showOptions = true,
        editCustomFilter,
        deleteCustomFilter,
        disabled = false,
        openState,
        setOpenState,
        disableSavedFilters,
        menuPopoverPosition,
        scrollable = false,
    }: FilterProps) => {
        const [filterName, setFilterName] = useState<string>('')
        const [loading, setLoading] = useState<boolean>(false)
        const [deleteConfirm, setDeleteConfirm] = useState<number>(0)
        Filters = Filters || useFilter(onChange, saveFilter)

        const onSave = (): void => {
            Filters.saveCustom(filterName)
            Filters.setSaveModalOpen(false)
        }

        const columns: Column<any, any>[] = [
            {
                key: 'name',
                cell: (item) => {
                    return (
                        <EditableField
                            value={item.name}
                            leftIcon={null}
                            type="text"
                            loading={loading}
                            onChange={(name) => {
                                setLoading(true)
                                editCustomFilter(item.id, item.filters, name).then(() => {
                                    setLoading(false)
                                })
                            }}
                        />
                    )
                },
            },
            {
                key: 'filters',
                cell: (item) => {
                    return (
                        <FiltersMenu
                            applyFilter
                            popoverPosition={Position.RIGHT_TOP}
                            showOptions={false}
                            editMode
                            editCustom={editCustomFilter}
                            filters={filters}
                            customFilters={[item]}
                            enableManage={false}
                            enableSave={false}
                        />
                    )
                },
            },
            {
                key: 'id',
                title: 'Delete',
                cell: (item) => (
                    <Button
                        disabled={loading}
                        onClick={() => setDeleteConfirm(item.id)}
                        icon="trash"
                    />
                ),
            },
        ]

        return (
            <Fragment>
                {!!customFilters && (
                    <Dialog
                        className="p-0"
                        isOpen={Filters.saveModalOpen}
                        onClose={() => Filters.setSaveModalOpen(false)}
                    >
                        <div className="px-3 pt-3 pb-0">
                            <h4 className="bp4-heading">New Saved Filter</h4>
                        </div>
                        <div className="p-3">
                            <p className="pb-3">
                                This will create a new saved filter from your currently selected
                                filters. Once saved, you can load a saved filter from the Saved
                                Filters menu
                            </p>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                }}
                            >
                                <ControlGroup className={'w-50'} fill={true} vertical={false}>
                                    <TextInput
                                        value={filterName}
                                        onChange={setFilterName}
                                        placeholder={'Filter name'}
                                        onKeyDown={(e) => (e.key === 'Enter' ? onSave() : null)}
                                    />
                                    <Popover
                                        interactionKind="hover"
                                        position="bottom"
                                        content={
                                            <div className="p-2">
                                                Enter a name to save this filter
                                            </div>
                                        }
                                        disabled={filterName != ''}
                                    >
                                        <Button
                                            disabled={filterName == ''}
                                            intent="primary"
                                            text="Save"
                                            onClick={onSave}
                                        />
                                    </Popover>
                                </ControlGroup>
                            </div>
                        </div>
                    </Dialog>
                )}
                {!!customFilters && (
                    <Dialog
                        canOutsideClickClose={false}
                        style={{ minWidth: 800 }}
                        className="p-0"
                        isOpen={Filters.manageModalOpen && customFilters.length > 0}
                        onClose={() => Filters.setManageModalOpen(false)}
                    >
                        <div className="px-3 pt-3 pb-0">
                            <h4 className="bp4-heading">Manage Saved Filters</h4>
                        </div>
                        <div className={Classes.DIALOG_BODY}>
                            <MyHTMLTable
                                empty={false}
                                paginationClassname={''}
                                htmlTableWrapperClassname={''}
                                reload={() => console.log('reload')}
                                mutateItems={[]}
                                onSelectChange={null}
                                selectKey={null}
                                showPerPage={false}
                                items={customFilters}
                                columns={columns}
                            />
                        </div>
                        <div className={`${Classes.DIALOG_FOOTER} mb-2`}>
                            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                                <Button
                                    intent="none"
                                    text="Close"
                                    onClick={() => Filters.setManageModalOpen(false)}
                                />
                            </div>
                        </div>
                    </Dialog>
                )}
                <Dialog
                    className="p-2"
                    isOpen={deleteConfirm != 0}
                    canOutsideClickClose={false}
                    onClose={() => setDeleteConfirm(0)}
                >
                    <div className={Classes.DIALOG_BODY}>
                        <p>
                            Are you sure you want to delete{' '}
                            {customFilters?.find((item) => item.id == deleteConfirm)?.name}?
                        </p>
                    </div>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                            <Button
                                onClick={() => setDeleteConfirm(0)}
                                intent="none"
                                text="Cancel"
                            />
                            <Button
                                onClick={() => {
                                    setLoading(true)
                                    setDeleteConfirm(0)
                                    deleteCustomFilter(deleteConfirm).then(() => setLoading(false))
                                }}
                                intent="danger"
                                text="Delete"
                            />
                        </div>
                    </div>
                </Dialog>
                <FiltersMenu
                    popoverPosition={menuPopoverPosition}
                    disabled={disabled}
                    defaultFilter={defaultFilter}
                    Filters={Filters}
                    showOptions={showOptions}
                    filters={filters}
                    customFilters={customFilters}
                    enableManage={enableManage}
                    enableSave={enableSave}
                    openState={openState}
                    setOpenState={setOpenState}
                    disableSavedFilters={disableSavedFilters}
                    scrollable={scrollable}
                />
            </Fragment>
        )
    },
)

function useControllableState<T>(value: T, setValue: (T) => any, initialValue: T): [T, (T) => any] {
    const [stateValue, setState] = useState<T>(initialValue)
    const effectiveValue = value !== undefined ? value : stateValue
    return [
        effectiveValue,
        (newValue: T): any => {
            setState(newValue)
            if (setValue) {
                setValue(newValue)
            }
        },
    ]
}

const FiltersMenu = observer(
    ({
        customFilters,
        showOptions, // whether to show Manage and Save options
        applyFilter = false, // If only one filter is provided, whether to apply on init (used in edit mode)
        editMode = false, // whether the we are in editMode. Based on that, it renders the respective actions in FilterMenu
        Filters, // this uses useFilter hook. If it's not provided, it will be created
        editCustom, // function for editing custom filters (used in edit mode)
        enableManage,
        enableSave,
        filters,
        popoverPosition = Position.BOTTOM_LEFT,
        defaultFilter,
        disabled = false,
        openState,
        setOpenState,
        disableSavedFilters = false,
        scrollable = false,
        triggerClassName = '',
    }: FiltersMenuProps): JSX.Element => {
        Filters =
            Filters ||
            useFilter(
                () => console.log('on change void'),
                () => console.log('save Filter void'),
                () => console.log('delete filter void'),
                (id: number, payload: any) => editCustom(id, payload),
            )

        const [open, setOpen] = useControllableState<boolean>(openState, setOpenState, false)
        const { favorites, setFavorites, toggleFavorite } = useFavoriteFilters()
        const [search, setSearch] = useState('')

        const clear = (): void => {
            Filters.clear()
            setOpen(false)
        }

        const apply = (): void => {
            Filters.apply()
            setOpen(false)
        }

        const edit = (id?: number, payload?: any): void => {
            Filters.editCustom(id ?? 0, payload)
            setOpen(false)
        }

        useEffect(() => {
            if (applyFilter) {
                Filters.loadCustom(customFilters, 0)
            }
        }, [])

        useEffect(() => {
            if (!!defaultFilter) {
                if (!Array.isArray(defaultFilter)) {
                    Filters.loadDefault(defaultFilter.filter, defaultFilter.value)
                } else {
                    Filters.loadDefaultMultiple(defaultFilter)
                }
            }
        }, [defaultFilter])

        const handleDirty = (filter: FilterI) => {
            return (e) => {
                const v = e.target && e.target.value ? e.target.value : e
                Filters.set(filter, v)
            }
        }

        const renderInput = (filter: FilterI): null | JSX.Element => {
            if (!Filters.isActive(filter)) {
                return null
            }

            switch (filter.type) {
                case 'select':
                    return (
                        <div className="mt-2">
                            <HTMLSelect
                                value={Filters.get(filter)}
                                onChange={handleDirty(filter)}
                                options={filter.options.map((option) => ({
                                    value: option.value,
                                    label: option.name,
                                }))}
                            />
                        </div>
                    )
                case 'select-component':
                    const Selector = filter.component
                    return (
                        <Selector
                            value={Filters.get(filter)}
                            onChange={(payload) => Filters.set(filter, payload)}
                        />
                    )
                case 'number':
                case 'nullable-number':
                    return (
                        <FilterNumber
                            filter={filter}
                            value={Filters.get(filter)}
                            onChange={(payload) => Filters.set(filter, payload)}
                            nullable={filter.type === 'nullable-number'}
                            apply={apply}
                        />
                    )
                case 'date':
                    return (
                        <FilterDate
                            filter={filter}
                            data={Filters.get(filter)}
                            onChange={(payload) => Filters.set(filter, payload)}
                            apply={apply}
                        />
                    )
                default:
                    console.error('Cannot renderInput for filter type', filter)
                    return null
            }
        }

        const renderFilter = (filter: FilterI): JSX.Element => {
            return (
                <div key={filter.key} className="bp4-menu-item group">
                    <div className="flex flex-col w-100">
                        <div className="flex justify-between">
                            <Checkbox
                                className="mb-0"
                                checked={Filters.isActive(filter)}
                                onChange={Filters.toggle(filter)}
                            />
                            <div className="flex-grow text-left ml-2">{filter.name}</div>
                            <Icon
                                className={classNames(
                                    'ml-2 transition-opacity duration-200 opacity-0 group-hover:opacity-100',
                                    {
                                        'text-gray-300': !favorites[filter.key],
                                        'text-yellow-300': favorites[filter.key],
                                    },
                                )}
                                icon={favorites[filter.key] ? 'star' : 'star-empty'}
                                onClick={() => {
                                    console.log('toggling', filter.key)
                                    toggleFavorite(filter.key)
                                }}
                            />
                        </div>
                        {renderInput(filter)}
                    </div>
                </div>
            )
        }

        const shownFilters = filters.filter(
            (filter) => filter.name.toLowerCase().indexOf(search.toLowerCase()) !== -1,
        )
        const favoriteFilters = shownFilters.filter((filter) => favorites[filter.key])
        const notFavoriteFilters = shownFilters.filter((filter) => !favorites[filter.key])
        const renderedFilters = (
            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                {favoriteFilters.length > 0 && (
                    <>
                        <div className="mt-2" />
                        <div className="favorites-section">
                            <div className="text-xs text-gray-500 px-3 py-1 font-medium">
                                Favorites
                            </div>
                            {favoriteFilters.map((filter) => renderFilter(filter))}
                        </div>
                        <div className="border-b my-2" />
                    </>
                )}
                <div>{notFavoriteFilters.map((filter) => renderFilter(filter))}</div>
            </div>
        )

        const menu = (
            <Menu>
                <div className="d-flex justify-content-between">
                    {editMode ? (
                        <MenuItem
                            icon="tick"
                            text="Save"
                            onClick={() =>
                                edit(customFilters[0].id, { ...Filters.current, ...Filters.dirty })
                            }
                        />
                    ) : (
                        <>
                            <MenuItem icon="cross" text="Clear" onClick={clear} />
                            <MenuItem icon="tick" text="Apply" onClick={apply} />
                        </>
                    )}
                </div>

                <MenuDivider />
                {!!customFilters && !disableSavedFilters && (
                    <Fragment>
                        <MenuItem text="Saved Filters" popoverProps={{ openOnTargetFocus: false }}>
                            {showOptions && (
                                <>
                                    <MenuItem
                                        disabled={!enableManage || customFilters.length === 0}
                                        icon="wrench"
                                        text="Manage"
                                        onClick={Filters.openManageModal}
                                    />
                                    <MenuItem
                                        disabled={!enableSave}
                                        icon="floppy-disk"
                                        text="Save"
                                        onClick={Filters.openSaveModal}
                                    />
                                    <MenuDivider />
                                </>
                            )}
                            {customFilters.map((filter, index) => (
                                <MenuItem
                                    key={index}
                                    text={filter.name}
                                    label={
                                        filter?.updated_at
                                            ? format(new Date(filter.updated_at), 'M/d/yy')
                                            : null
                                    }
                                    onClick={() => Filters.loadCustom(customFilters, index)}
                                />
                            ))}
                        </MenuItem>
                        <MenuDivider />
                    </Fragment>
                )}
                <div className="mx-1 border-b">
                    <TextInput
                        autoFocus
                        className="mb-2 mt-1"
                        value={search}
                        onChange={setSearch}
                        placeholder="Search"
                        onKeyDown={(e) => (e.key === 'Enter' ? apply() : null)}
                    />
                    {!!search && (
                        <div className="text-gray-300 mb-2 text-xs">
                            Showing {shownFilters.length} / {filters.length} columns
                        </div>
                    )}
                </div>
                {scrollable ? (
                    <div className="overflow-scroll overflow-x-hidden h-80">{renderedFilters}</div>
                ) : (
                    renderedFilters
                )}
            </Menu>
        )

        return (
            <Popover
                content={menu}
                isOpen={open}
                position={popoverPosition}
                interactionKind="click"
                onClose={() => {
                    setOpen(false)
                }}
            >
                <Button
                    className={`rounded-lg ${triggerClassName}`}
                    minimal
                    outlined
                    disabled={disabled}
                    icon={<FilterIcon />}
                    text={'Filter'}
                    onClick={() => {
                        setOpen(!open)
                    }}
                />
            </Popover>
        )
    },
)
